import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { toast } from 'react-hot-toast';
import zeiterfassungService from '../services/zeiterfassungService';
import abwesenheitService from '../services/abwesenheitService';
import feiertagService from '../services/feiertagService';
import { Icon, PageHeader, LoadingScreen, EmptyState, fehlerText, th } from '../components/ui';
import { ankunft, markiereNeu } from '../utils/ankunft';
import { AbwesenheitTyp, Feiertag, FeiertagStatus, Mitarbeiter } from '../types';
import { DatumFeld, UhrzeitFeld } from '../components/DatumZeit';

const heute = () => new Date().toLocaleDateString('sv-SE');
const aktuellesJahr = new Date().getFullYear();
const WOCHENTAGE: { code: string; label: string }[] = [
  { code: 'MO', label: 'Mo' }, { code: 'DI', label: 'Di' }, { code: 'MI', label: 'Mi' }, { code: 'DO', label: 'Do' },
  { code: 'FR', label: 'Fr' }, { code: 'SA', label: 'Sa' }, { code: 'SO', label: 'So' },
];

const InfoTip: React.FC<{ text: string }> = ({ text }) => (
  <span className="relative inline-flex items-center group align-middle ml-1">
    <span className="w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 text-white text-[10px] font-bold cursor-help select-none">i</span>
    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 w-60 rounded-lg bg-gray-900 text-white text-xs font-normal normal-case leading-snug p-2.5 opacity-0 group-hover:opacity-100 transition-opacity z-20 shadow-lg">
      {text}
    </span>
  </span>
);

interface Row {
  userId: number;
  name: string;
  wochenstunden: number;
  tagesbeginn: string;
  tagesende: string;
  arbeitstage: string[];
  anspruchTage: number;
  uebertragTage: number;
  ausgeblendet: boolean;
}

const Personalkonfiguration: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [zeigeAusgeblendete, setZeigeAusgeblendete] = useState(false);

  // Abwesenheit anlegen
  const [users, setUsers] = useState<Mitarbeiter[]>([]);
  const [absUser, setAbsUser] = useState<number | ''>('');
  const [absTyp, setAbsTyp] = useState<AbwesenheitTyp>('KRANKHEIT');
  const [absVon, setAbsVon] = useState(heute());
  const [absBis, setAbsBis] = useState(heute());

  // Feiertage
  const [feiertage, setFeiertage] = useState<Feiertag[]>([]);
  const [statusAktuell, setStatusAktuell] = useState<FeiertagStatus | null>(null);
  const [statusNaechstes, setStatusNaechstes] = useState<FeiertagStatus | null>(null);

  // Stempel-QR-Token
  const [stempelToken, setStempelToken] = useState('');
  const stempelUrl = stempelToken ? `${window.location.origin}/stempeln/${stempelToken}` : '';
  const qrRef = useRef<HTMLCanvasElement>(null);
  const qrPrintRef = useRef<HTMLCanvasElement>(null);

  // Stempel-IP-Beschränkung
  const [ipAllowlist, setIpAllowlist] = useState('');
  const [meineIp, setMeineIp] = useState('');

  const laden = useCallback(async () => {
    setLoading(true);
    try {
      const konfigs = await zeiterfassungService.getKonfigurationen();
      const merged = await Promise.all(konfigs.map(async (k) => {
        const s = await abwesenheitService.saldoFuerUser(k.userId, aktuellesJahr).catch(() => null);
        return {
          userId: k.userId,
          name: k.mitarbeiterName,
          wochenstunden: k.wochenstunden,
          tagesbeginn: k.tagesbeginn ?? '',
          tagesende: k.tagesende ?? '',
          arbeitstage: k.arbeitstage ?? ['MO', 'DI', 'MI', 'DO', 'FR'],
          anspruchTage: s?.anspruchTage ?? 20,
          uebertragTage: s?.uebertragTage ?? 0,
          ausgeblendet: k.ausgeblendet,
        } as Row;
      }));
      setRows(merged);
    } catch {
      toast.error('Konfiguration konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, []);

  const ladeFeiertage = useCallback(async () => {
    try {
      const [f, sA, sN] = await Promise.all([
        feiertagService.getByJahr(aktuellesJahr),
        feiertagService.status(aktuellesJahr),
        feiertagService.status(aktuellesJahr + 1),
      ]);
      setFeiertage(f); setStatusAktuell(sA); setStatusNaechstes(sN);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    laden();
    ladeFeiertage();
    zeiterfassungService.getAktiveMitarbeiter().then(setUsers).catch(() => {});
    zeiterfassungService.getStempelToken().then((r) => setStempelToken(r.token)).catch(() => {});
    zeiterfassungService.getStempelIp().then((r) => { setIpAllowlist(r.allowlist); setMeineIp(r.meineIp); }).catch(() => {});
  }, [laden, ladeFeiertage]);

  const ipSpeichern = async () => {
    try {
      await zeiterfassungService.setStempelIp(ipAllowlist);
      toast.success(ipAllowlist.trim() ? 'IP-Beschränkung gespeichert' : 'IP-Beschränkung aufgehoben (alle IPs erlaubt)');
    } catch (e) {
      toast.error(fehlerText(e, 'IP-Beschränkung konnte nicht gespeichert werden'));
    }
  };

  const meineIpHinzufuegen = () => {
    if (!meineIp) return;
    const vorhanden = ipAllowlist.split(',').map((s) => s.trim()).filter(Boolean);
    if (vorhanden.includes(meineIp)) return;
    setIpAllowlist([...vorhanden, meineIp].join(', '));
  };

  const kopiereStempelUrl = () => {
    if (!stempelUrl) return;
    navigator.clipboard?.writeText(stempelUrl);
    toast.success('Link kopiert');
  };

  // Hochauflösenden Canvas fürs Drucken/Herunterladen verwenden (scharf statt hochskaliert)
  const qrPng = (): string | null => (qrPrintRef.current ?? qrRef.current)?.toDataURL('image/png') ?? null;

  const qrHerunterladen = () => {
    const data = qrPng();
    if (!data) return;
    const a = document.createElement('a');
    a.href = data;
    a.download = 'stempel-qr.png';
    a.click();
  };

  const qrDrucken = () => {
    const data = qrPng();
    if (!data) return;
    const w = window.open('', '_blank');
    if (!w) { toast.error('Pop-up wurde blockiert'); return; }
    w.document.write(
      `<html><head><title>Stempeluhr QR-Code</title></head>` +
      `<body style="text-align:center;font-family:sans-serif;padding:40px">` +
      `<h2>Stempeluhr – Ein-/Ausstempeln</h2>` +
      `<img src="${data}" style="width:320px;height:320px;image-rendering:pixelated" alt="QR-Code" />` +
      `<script>window.onload=function(){window.print()}</script></body></html>`
    );
    w.document.close();
  };

  const tokenNeuErzeugen = async () => {
    if (!window.confirm('Neuen Stempel-Code erzeugen? Der bisherige QR-Code wird damit ungültig.')) return;
    try {
      const r = await zeiterfassungService.regenerateStempelToken();
      setStempelToken(r.token);
      toast.success('Stempel-Code neu erzeugt – bitte QR-Code neu ausdrucken');
    } catch (e) {
      toast.error(fehlerText(e, 'Stempel-Code konnte nicht neu erzeugt werden'));
    }
  };

  const setRow = (userId: number, patch: Partial<Row>) =>
    setRows((prev) => prev.map((r) => (r.userId === userId ? { ...r, ...patch } : r)));

  const arbeitstagToggle = (r: Row, code: string) => {
    const next = r.arbeitstage.includes(code) ? r.arbeitstage.filter((c) => c !== code) : [...r.arbeitstage, code];
    setRow(r.userId, { arbeitstage: next });
  };

  const speichern = async (r: Row) => {
    if (r.arbeitstage.length === 0) { toast.error('Mindestens ein Arbeitstag muss gewählt sein'); return; }
    try {
      await zeiterfassungService.setKonfiguration(r.userId, {
        wochenstunden: r.wochenstunden,
        tagesbeginn: r.tagesbeginn || null,
        tagesende: r.tagesende || null,
        arbeitstage: r.arbeitstage,
      });
      await abwesenheitService.setAnspruch(r.userId, { jahr: aktuellesJahr, anspruchTage: r.anspruchTage, uebertragTage: r.uebertragTage });
      toast.success(`Konfiguration für ${r.name} gespeichert`);
      markiereNeu(`personal-${r.userId}`);
    } catch (e: any) {
      toast.error(fehlerText(e, 'Konfiguration konnte nicht gespeichert werden'));
    }
  };

  const ausblendenSetzen = async (r: Row, ausgeblendet: boolean) => {
    try {
      await zeiterfassungService.setKonfigurationAusgeblendet(r.userId, ausgeblendet);
      setRow(r.userId, { ausgeblendet });
      toast.success(ausgeblendet ? `${r.name} ausgeblendet` : `${r.name} wieder eingeblendet`);
      if (!ausgeblendet) markiereNeu(`personal-${r.userId}`);
    } catch (e) {
      toast.error(fehlerText(e, 'Sichtbarkeit konnte nicht gespeichert werden'));
    }
  };

  const abwesenheitAnlegen = async (e: React.FormEvent) => {
    e.preventDefault();
    if (absUser === '') { toast.error('Bitte einen Mitarbeiter wählen'); return; }
    try {
      await abwesenheitService.anlegen({ userId: absUser, typ: absTyp, von: absVon, bis: absBis });
      toast.success('Abwesenheit angelegt');
    } catch (err: any) {
      toast.error(fehlerText(err, 'Abwesenheit konnte nicht angelegt werden'));
    }
  };

  const feiertageAbrufen = async (jahr: number) => {
    try {
      const res = await feiertagService.abrufen(jahr);
      toast.success(`${res.anzahl} Feiertage für ${jahr} gespeichert`);
      await ladeFeiertage();
    } catch {
      toast.error('Feiertage konnten nicht abgerufen werden (Feiertags-API nicht erreichbar?)');
    }
  };

  return (
    <div className="p-4 md:p-6 space-y-6">
      <PageHeader title="Personal-Konfiguration" className="!mb-0" />

      {/* Arbeitszeit + Urlaubsanspruch */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <h2 className="font-semibold text-gray-900 p-4 border-b">Arbeitszeit & Urlaubsanspruch ({aktuellesJahr})</h2>
        {loading ? (
          <LoadingScreen />
        ) : (
          <table className="min-w-full text-sm">
            <thead>
              <tr>
                <th className={th}>Mitarbeiter</th>
                <th className={th}>Wochenstd.</th>
                <th className={th}>
                  Arbeitstage
                  <InfoTip text="Tage, an denen gearbeitet wird. Das Tages-Soll ist Wochenstunden geteilt durch die Anzahl der Arbeitstage. Freie Tage erzeugen keine Minusstunden." />
                </th>
                <th className={th}>Tagesbeginn</th>
                <th className={th}>Tagesende</th>
                <th className={th}>
                  Urlaub (T)
                  <InfoTip text="Jahres-Urlaubsanspruch in Tagen (gesetzliches Minimum 20 bei 5-Tage-Woche, §3 BUrlG)." />
                </th>
                <th className={th}>
                  Übertrag (T)
                  <InfoTip text="Resturlaub aus dem Vorjahr in Tagen. Wird zum Jahresanspruch addiert. Verfällt nach §7 Abs. 3 BUrlG i. d. R. am 31.03. Derzeit manuell einzutragen." />
                </th>
                <th className={th}></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.filter((r) => !r.ausgeblendet).map((r) => { const glow = ankunft(`personal-${r.userId}`); return (
                <tr key={r.userId} ref={glow.ref} className={glow.className}>
                  <td className="px-4 py-2 font-medium text-gray-900 whitespace-nowrap">{r.name}</td>
                  <td className="px-4 py-2"><input type="number" step="0.5" min="0" max="60" value={r.wochenstunden} onChange={(e) => setRow(r.userId, { wochenstunden: Number(e.target.value) })} className="w-20 p-1.5 border rounded-lg" /></td>
                  <td className="px-4 py-2">
                    <div className="flex gap-1">
                      {WOCHENTAGE.map((w) => {
                        const aktiv = r.arbeitstage.includes(w.code);
                        return (
                          <button
                            key={w.code}
                            type="button"
                            onClick={() => arbeitstagToggle(r, w.code)}
                            className={`w-8 h-8 rounded-full text-xs font-medium border transition-colors ${aktiv ? 'bg-brand-600 border-brand-600 text-white' : 'bg-white border-gray-300 text-gray-400 hover:border-gray-400'}`}
                          >
                            {w.label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-1">
                      {r.arbeitstage.length > 0 ? `Soll ${(r.wochenstunden / r.arbeitstage.length).toFixed(1).replace('.', ',')} h/Tag` : 'kein Arbeitstag'}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <UhrzeitFeld value={r.tagesbeginn} onChange={(v) => setRow(r.userId, { tagesbeginn: v })} className="p-1.5 border rounded-lg" />
                      {r.tagesbeginn && <button type="button" onClick={() => setRow(r.userId, { tagesbeginn: '' })} title="Zurücksetzen" className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Zurücksetzen"><Icon.Close className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-1">
                      <UhrzeitFeld value={r.tagesende} onChange={(v) => setRow(r.userId, { tagesende: v })} className="p-1.5 border rounded-lg" />
                      {r.tagesende && <button type="button" onClick={() => setRow(r.userId, { tagesende: '' })} title="Zurücksetzen" className="w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600" aria-label="Zurücksetzen"><Icon.Close className="h-3.5 w-3.5" /></button>}
                    </div>
                  </td>
                  <td className="px-4 py-2"><input type="number" step="0.5" min="0" value={r.anspruchTage} onChange={(e) => setRow(r.userId, { anspruchTage: Number(e.target.value) })} className="w-20 p-1.5 border rounded-lg" /></td>
                  <td className="px-4 py-2"><input type="number" step="0.5" min="0" value={r.uebertragTage} onChange={(e) => setRow(r.userId, { uebertragTage: Number(e.target.value) })} className="w-20 p-1.5 border rounded-lg" /></td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button onClick={() => speichern(r)} className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs">Speichern</button>
                    <button onClick={() => ausblendenSetzen(r, true)} title="Kein Mitarbeiter – aus dieser Übersicht ausblenden" className="ml-2 px-3 py-1.5 rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-xs">Ausblenden</button>
                  </td>
                </tr>
              ); })}
              {rows.filter((r) => !r.ausgeblendet).length === 0 && (
                <tr><td colSpan={8}><EmptyState icon={<Icon.Kunden className="h-6 w-6" />} title="Keine Mitarbeiter sichtbar" hint="Ausgeblendete Benutzer lassen sich unten wieder einblenden; neue Benutzer legen Sie in der Benutzerverwaltung an." /></td></tr>
              )}
            </tbody>
          </table>
        )}

        {/* Ausgeblendete Benutzer (Admin/System-User ohne Arbeitszeit) */}
        {rows.some((r) => r.ausgeblendet) && (
          <div className="border-t bg-gray-50">
            <button
              onClick={() => setZeigeAusgeblendete((v) => !v)}
              className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-medium text-gray-500 hover:text-gray-700"
            >
              <span className="flex items-center gap-1">{zeigeAusgeblendete ? <Icon.ChevronDown className="h-3.5 w-3.5" /> : <Icon.ChevronRight className="h-3.5 w-3.5" />} Ausgeblendete Benutzer ({rows.filter((r) => r.ausgeblendet).length})</span>
            </button>
            {zeigeAusgeblendete && (
              <ul className="ui-aufklappen px-4 pb-3 space-y-1.5">
                {rows.filter((r) => r.ausgeblendet).map((r) => (
                  <li key={r.userId} className="flex items-center justify-between text-sm py-1">
                    <span className="text-gray-600">{r.name}</span>
                    <button onClick={() => ausblendenSetzen(r, false)} className="px-3 py-1.5 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50 text-xs">Einblenden</button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* Abwesenheit eintragen */}
      <form onSubmit={abwesenheitAnlegen} className="bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold text-gray-900 mb-3">Abwesenheit eintragen (z. B. Krankheit)</h2>
        <div className="flex flex-wrap items-end gap-4">
          <div><label className="block text-sm font-medium mb-1">Mitarbeiter</label>
            <select value={absUser} onChange={(e) => setAbsUser(e.target.value === '' ? '' : Number(e.target.value))} className="p-2 border border-gray-300 rounded-lg min-w-[180px]">
              <option value="">Bitte wählen</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.firstName} {u.lastName}</option>)}
            </select>
          </div>
          <div><label className="block text-sm font-medium mb-1">Typ</label>
            <select value={absTyp} onChange={(e) => setAbsTyp(e.target.value as AbwesenheitTyp)} className="p-2 border border-gray-300 rounded-lg">
              <option value="KRANKHEIT">Krankheit</option>
              <option value="SONDERURLAUB">Sonderurlaub</option>
              <option value="UNBEZAHLT">Unbezahlt</option>
              <option value="URLAUB">Urlaub</option>
            </select>
          </div>
          <div><label className="block text-sm font-medium mb-1">Von</label><DatumFeld value={absVon} onChange={(v) => { setAbsVon(v); if (absBis < v) setAbsBis(v); }} className="p-2 border border-gray-300 rounded-lg" /></div>
          <div><label className="block text-sm font-medium mb-1">Bis</label><DatumFeld value={absBis} min={absVon} onChange={(v) => setAbsBis(v)} className="p-2 border border-gray-300 rounded-lg" /></div>
          <button type="submit" className="bg-brand-600 text-white px-4 py-2 rounded-lg hover:bg-brand-700">Abwesenheit anlegen</button>
        </div>
      </form>

      {/* Stempel-QR-Code */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold text-gray-900 mb-1">Stempel-QR-Code</h2>
        <p className="text-xs text-gray-500 mb-3">
          Diesen Link als QR-Code ausdrucken und am Terminal in der Firma aufhängen. Mitarbeiter stempeln ausschließlich über diesen QR-Code – ohne gültigen Code ist kein Stempeln möglich.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            readOnly
            value={stempelUrl}
            onFocus={(e) => e.target.select()}
            className="flex-1 min-w-[260px] p-2 border border-gray-300 rounded-lg font-mono text-xs bg-gray-50"
          />
          <button onClick={kopiereStempelUrl} className="text-xs px-3 py-2 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50">Link kopieren</button>
          <button onClick={tokenNeuErzeugen} className="text-xs px-3 py-2 rounded-lg border border-red-300 text-red-600 hover:bg-red-50">Code neu erzeugen</button>
        </div>

        {stempelUrl && (
          <div className="mt-4 flex flex-col sm:flex-row items-start gap-4">
            <div className="p-3 bg-white border border-gray-200 rounded-lg">
              <QRCodeCanvas ref={qrRef} value={stempelUrl} size={160} level="M" includeMargin />
              {/* Versteckter hochaufloesender Canvas fuer scharfen Druck/Download */}
              <QRCodeCanvas ref={qrPrintRef} value={stempelUrl} size={1024} level="M" includeMargin style={{ display: 'none' }} />
            </div>
            <div className="flex flex-col gap-2">
              <button onClick={qrDrucken} className="text-xs px-3 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700">QR-Code drucken</button>
              <button onClick={qrHerunterladen} className="text-xs px-3 py-2 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50">Als PNG herunterladen</button>
            </div>
          </div>
        )}

        <p className="text-xs text-amber-600 mt-3">
          „Code neu erzeugen" macht den bisherigen QR-Code sofort ungültig – danach unbedingt einen neuen QR-Code ausdrucken.
        </p>
      </div>

      {/* Stempel nur aus dem Firmennetz */}
      <div className="bg-white rounded-lg shadow p-4">
        <h2 className="font-semibold text-gray-900 mb-1">Stempeln nur aus dem Firmennetz</h2>
        <p className="text-xs text-gray-500 mb-3">
          Erlaubte IP-Adressen oder Netzbereiche (CIDR), kommagetrennt – z. B. die öffentliche IP Ihres Firmen-Internetanschlusses.
          <b> Leer = keine Beschränkung</b> (Stempeln von überall möglich).
        </p>
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <input
            value={ipAllowlist}
            onChange={(e) => setIpAllowlist(e.target.value)}
            placeholder="z. B. 203.0.113.7, 203.0.113.0/24"
            className="flex-1 min-w-[260px] p-2 border border-gray-300 rounded-lg font-mono text-xs"
          />
          <button onClick={ipSpeichern} className="text-xs px-3 py-2 rounded-lg bg-brand-600 text-white hover:bg-brand-700">Speichern</button>
        </div>
        {meineIp && (
          <p className="text-xs text-gray-500">
            Ihre aktuelle IP: <span className="font-mono">{meineIp}</span>
            <button onClick={meineIpHinzufuegen} className="ml-2 text-brand-600 hover:underline">Hinzufügen</button>
          </p>
        )}
        <p className="text-xs text-amber-600 mt-2">
          Achtung: Tragen Sie hier nur die IP Ihres Firmen-Anschlusses ein. Bei dynamischer IP kann sich diese gelegentlich ändern und muss dann angepasst werden.
        </p>
      </div>

      {/* Feiertage */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <h2 className="font-semibold text-gray-900">Feiertage (Niedersachsen)</h2>
          <div className="flex gap-2">
            <button onClick={() => feiertageAbrufen(aktuellesJahr)} className="text-xs px-3 py-1.5 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50">{aktuellesJahr} abrufen</button>
            <button onClick={() => feiertageAbrufen(aktuellesJahr + 1)} className="text-xs px-3 py-1.5 rounded-lg border border-brand-600 text-brand-700 hover:bg-brand-50">{aktuellesJahr + 1} abrufen</button>
          </div>
        </div>
        {statusNaechstes && !statusNaechstes.vollstaendig && (
          <div className="mb-3 text-sm bg-amber-50 text-amber-800 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <Icon.Warnung className="h-4 w-4 mt-0.5 flex-shrink-0" /><span>Für {statusNaechstes.jahr} sind noch keine (vollständigen) Feiertage hinterlegt ({statusNaechstes.anzahl}). Bitte abrufen.</span>
          </div>
        )}
        <p className="text-xs text-gray-500 mb-2">{statusAktuell?.anzahl ?? 0} Feiertage für {aktuellesJahr}</p>
        <div className="flex flex-wrap gap-2">
          {feiertage.map((f) => (
            <span key={f.id} className="text-xs px-2 py-1 rounded-full bg-purple-50 text-purple-700">{new Date(f.datum + 'T00:00:00').toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })} {f.name}</span>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Personalkonfiguration;
